import React from 'react';
import {
  Bot,
  Globe,
  Zap,
  BarChart3,
  MessageCircle,
  Clock,
  Target,
  Users,
  CheckCircle,
  Info,
  Lightbulb,
  TrendingUp,
  Activity,
  Heart,
  Search,
  Filter,
  Settings,
  Sparkles
} from 'lucide-react';

const AdditionalFeature = () => {
  const ExampleBox = ({ children }) => (
    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4 rounded-r-lg">
      <div className="flex items-start">
        <Info className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-blue-800 mb-1">Example</p>
          <div className="text-blue-700 text-sm">{children}</div>
        </div>
      </div>
    </div>
  );

  const ProTip = ({ children }) => (
    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4 rounded-r-lg">
      <div className="flex items-start">
        <Lightbulb className="w-5 h-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-yellow-800 mb-1">Pro Tip</p>
          <div className="text-yellow-700 text-sm">{children}</div>
        </div>
      </div>
    </div>
  );

  const FeatureCard = ({ icon: Icon, title, description, capabilities, benefits, gradient }) => (
    <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
      <div className="flex items-center mb-6">
        <div className={`${gradient} rounded-lg p-2 mr-4`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
      </div>

      <p className="text-gray-600 mb-6">{description}</p>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Capabilities</h3>
          <ul className="space-y-3">
            {capabilities.map((capability, index) => (
              <li key={index} className="flex items-start">
                <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium text-gray-900">{capability.title}</span>
                  <p className="text-sm text-gray-600 mt-1">{capability.description}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Why You'll Love It</h3>
          <ul className="space-y-2">
            {benefits.map((benefit, index) => (
              <li key={index} className="flex items-start">
                <Sparkles className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );

  const features = [
    {
      icon: Bot,
      title: "Automated Chat with User-Specific Customization",
      description: "Imagine having a personal assistant that responds to customer queries instantly, 24/7. That's exactly what our Automated Chat feature offers. It's not just automation—it's personalization at scale!",
      gradient: "bg-gradient-to-br from-purple-500 to-pink-500",
      capabilities: [
        {
          title: "Custom FAQs",
          description: "Create a library of Frequently Asked Questions tailored to your business needs. Examples: 'What are your operating hours?' or 'How can I track my order?'"
        },
        {
          title: "Toggle FAQs On/Off",
          description: "Control which FAQs are active at any time. Example: Deactivate seasonal FAQs like 'Black Friday Deals' when they're no longer relevant."
        },
        {
          title: "User-Specific Automation",
          description: "Target individual users with custom responses. Enable automation for specific customer groups only."
        }
      ],
      benefits: [
        "Save hours of time by automating repetitive queries",
        "Delight customers with instant, accurate responses",
        "Stay professional even during off-hours"
      ]
    },
    {
      icon: Globe,
      title: "Multi-Platform Integration: One Dashboard, All Platforms",
      description: "Tired of switching between apps to manage messages? With ChatInject, you can integrate Instagram, Facebook, WhatsApp, and Telegram into one seamless interface.",
      gradient: "bg-gradient-to-br from-blue-500 to-cyan-500",
      capabilities: [
        {
          title: "Unified Management",
          description: "View and respond to messages across all platforms from a single inbox. Filter conversations by platform or status (e.g., unread, priority)."
        },
        {
          title: "Cross-Platform Automation",
          description: "Set up one automation workflow and apply it to all platforms simultaneously. Example: A 'Thank you for reaching out!' message can greet users across all platforms instantly."
        },
        {
          title: "Centralized Analytics",
          description: "Compare performance metrics across all platforms in one place. See how Instagram engagement compares to WhatsApp message response rates."
        }
      ],
      benefits: [
        "Simplifies management, saving you time and effort",
        "Provides consistent communication across all platforms",
        "Helps you focus on what matters: building strong customer relationships"
      ]
    },
    {
      icon: Zap,
      title: "Quick Reply Integration: Speed Up Conversations",
      description: "Don't type the same message over and over. ChatInject's Quick Reply Integration lets you respond faster without sacrificing quality.",
      gradient: "bg-gradient-to-br from-orange-500 to-red-500",
      capabilities: [
        {
          title: "Pre-Defined Replies",
          description: "Create a library of commonly used responses (e.g., 'Thank you for your inquiry!'). Accessible across all platforms for instant use."
        },
        {
          title: "Smart Dropdowns",
          description: "Type / in the chatbox to see a list of quick replies. Search or scroll to select the perfect response."
        },
        {
          title: "Easy Editing",
          description: "Update or add new quick replies in seconds. Customize replies for special promotions or campaigns."
        }
      ],
      benefits: [
        "Cuts response time dramatically",
        "Ensures consistency in your messaging",
        "Keeps your communication professional and polished"
      ]
    },
    {
      icon: BarChart3,
      title: "Real-Time Analytics: Insights That Drive Growth",
      description: "Data drives decisions, and with ChatInject's real-time analytics, you're always in the know.",
      gradient: "bg-gradient-to-br from-emerald-500 to-teal-500",
      capabilities: [
        {
          title: "Message Response Times",
          description: "Measure how quickly you're responding to customers. Example: 'You've improved your average response time by 20% this week!'"
        },
        {
          title: "Engagement Metrics",
          description: "Track likes, shares, and comments on Instagram and Facebook. Monitor WhatsApp message volume and trends."
        },
        {
          title: "Customer Sentiment",
          description: "Analyze feedback to understand how customers feel about your interactions. Example: '95% of customers rated your responses as helpful!'"
        }
      ],
      benefits: [
        "Helps you identify strengths and areas for improvement",
        "Provides actionable insights to boost engagement and satisfaction",
        "Tracks progress over time, showing how your efforts are paying off"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 mt-[32px]">
      <div className=" ">
        {/* Header Section */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="text-left">
            <h1 className="text-2xl font-bold text-gray-900 mb-6">
              Advanced Features: Unlock the Full Power of ChatInject
            </h1>
            <p className="text-lg text-gray-600 mx-auto">
              ChatInject isn't just a tool; it's your gateway to smarter, faster, and more personalized communication.
              Our advanced features are designed to empower your business with cutting-edge tools that make managing
              your social media accounts effortless and effective.
            </p>
          </div>
        </div>

        {/* Features Section */}
        {features.map((feature, index) => (
          <FeatureCard
            key={index}
            icon={feature.icon}
            title={feature.title}
            description={feature.description}
            gradient={feature.gradient}
            capabilities={feature.capabilities}
            benefits={feature.benefits}
          />
        ))}

        {/* Interactive Examples Section */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="bg-indigo-100 rounded-lg p-2 mr-4">
              <Target className="w-6 h-6 text-indigo-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">See It in Action</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <ExampleBox>
              <strong>Automated Response Example:</strong><br />
              Customer: "What are your business hours?"<br />
              <em>Auto-reply:</em> "Hi! Thanks for reaching out. We're open Monday-Friday 9AM-6PM, and Saturday 10AM-4PM. How can we help you today?"
            </ExampleBox>

            <ExampleBox>
              <strong>Quick Reply in Action:</strong><br />
              Type "/" in chat → Select "Order Status" →
              <em>Instantly sends:</em> "I'd be happy to help you track your order! Please provide your order number and I'll get that information for you right away."
            </ExampleBox>
          </div>

          <ProTip>
            Combine automated chat with quick replies for maximum efficiency. Set up automation for common questions, then use quick replies for personalized follow-ups!
          </ProTip>
        </div>

        {/* Analytics Dashboard Preview */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="bg-emerald-100 rounded-lg p-2 mr-4">
              <Activity className="w-6 h-6 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Analytics at a Glance</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 text-center">
              <Clock className="w-8 h-8 text-blue-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Response Time</h3>
              <div className="text-2xl font-bold text-blue-600 mb-1">2.3 min</div>
              <p className="text-sm text-gray-600">Average response time</p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-6 text-center">
              <TrendingUp className="w-8 h-8 text-green-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Engagement</h3>
              <div className="text-2xl font-bold text-green-600 mb-1">+34%</div>
              <p className="text-sm text-gray-600">Increase this month</p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-6 text-center">
              <Heart className="w-8 h-8 text-purple-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Satisfaction</h3>
              <div className="text-2xl font-bold text-purple-600 mb-1">96%</div>
              <p className="text-sm text-gray-600">Customer satisfaction</p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-lg shadow-lg p-8 text-white text-center">
          <div className="bg-white bg-opacity-20 rounded-full p-3 w-16 h-16 mx-auto mb-6 flex items-center justify-center">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold mb-4">Ready to Transform Your Communication?</h2>
          <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
            These advanced features are designed to scale with your business. Start with the basics and unlock more
            powerful capabilities as you grow.
          </p>
          <div className="bg-white bg-opacity-10 rounded-lg p-6 max-w-2xl mx-auto">
            <h3 className="text-lg font-semibold mb-4">What's Next?</h3>
            <div className="grid md:grid-cols-2 gap-4 text-left">
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-300 mr-2 mt-0.5" />
                <span className="text-blue-100 text-sm">Set up your first automation workflow</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-300 mr-2 mt-0.5" />
                <span className="text-blue-100 text-sm">Create your quick reply library</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-300 mr-2 mt-0.5" />
                <span className="text-blue-100 text-sm">Connect multiple platforms</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-300 mr-2 mt-0.5" />
                <span className="text-blue-100 text-sm">Monitor your analytics dashboard</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-white rounded-lg shadow-sm p-6 text-center mt-8">
          <p className="text-gray-600 mb-4">
            Need help setting up these advanced features? Our support team is ready to guide you through every step.
          </p>
          <div className="flex justify-center space-x-4 text-sm">
            <a href="#" className="text-blue-600 hover:text-blue-800 transition-colors">Feature Documentation</a>
            <span className="text-gray-300">|</span>
            <a href="#" className="text-blue-600 hover:text-blue-800 transition-colors">Video Tutorials</a>
            <span className="text-gray-300">|</span>
            <a href="#" className="text-blue-600 hover:text-blue-800 transition-colors">Get Support</a>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default AdditionalFeature;